import React from 'react';
import { AlertTriangle } from 'lucide-react';

// Renders an analyzed-document digest as a business-readable block: section
// headings, two-column detail rows and small tables — never raw JSON.
//
// The backend humanises every findings section into lines shaped like:
//   "Section name:"            a heading
//   "  Label: value"           a detail row
//   "  Rows (3):"              a table of rows
//       "- Acme Foods (revenue: $42,000)"
// so the presentation stays honest to whatever was actually recorded.

const isHeading = (line) => /^\S.*:$/.test(line.trim()) && !line.trim().startsWith('-');
const isRowGroup = (line) => /\((\d+)\):\s*$/.test(line.trim());
const isItem = (line) => /^\s*-\s+/.test(line);
const isNote = (line) => /^\s*\(\+\d+ more\)/.test(line);

// "Name (a: 1; b: 2)" -> { label: 'Name', meta: ['a: 1', 'b: 2'] }
function parseItem(line) {
  const text = line.replace(/^\s*-\s+/, '').trim();
  const match = text.match(/^(.*?)\s*\((.*)\)$/);
  if (!match) return { label: text, meta: [] };
  return {
    label: match[1],
    meta: match[2].split(';').map((m) => m.trim()).filter(Boolean),
  };
}

function DetailRows({ rows }) {
  return (
    <dl className="divide-y divide-slate-100">
      {rows.map((row, i) => {
        const idx = row.indexOf(':');
        const label = idx > 0 ? row.slice(0, idx).trim() : '';
        const value = idx > 0 ? row.slice(idx + 1).trim() : row.trim();
        return (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-1 py-1.5">
            <dt className="text-xs font-medium text-slate-500">{label || '\u2014'}</dt>
            <dd className={`text-xs text-navy-900 sm:col-span-2 ${/^[-\d$.,%]+$/.test(value) ? 'tabular-nums font-medium' : ''}`}>
              {value}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function ItemTable({ title, items, note }) {
  const parsed = items.map(parseItem);
  return (
    <div className="mt-2">
      {title && (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
          {title}
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <tbody className="divide-y divide-slate-100">
            {parsed.map((item, i) => (
              <tr key={i} className="bg-white">
                <td className="px-3 py-1.5 font-medium text-navy-900 align-top">{item.label}</td>
                <td className="px-3 py-1.5 text-slate-600 align-top">
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                    {item.meta.map((m, j) => {
                      const at = m.indexOf(':');
                      if (at < 0) return <span key={j}>{m}</span>;
                      return (
                        <span key={j}>
                          <span className="text-slate-400">{m.slice(0, at)}</span>{' '}
                          <span className={/^[-\d$.,%]+$/.test(m.slice(at + 1).trim()) ? 'tabular-nums text-navy-900' : ''}>
                            {m.slice(at + 1).trim()}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {note && <p className="mt-1 text-[11px] text-slate-400">{note}</p>}
    </div>
  );
}

export default function DigestView({ text, emptyLabel = 'No analysis recorded yet.', maxHeight }) {
  const lines = String(text || '').split('\n').filter((l) => l.trim() !== '');
  if (lines.length === 0) {
    return <p className="text-xs text-slate-400">{emptyLabel}</p>;
  }

  const blocks = [];
  let detail = [];
  const flushDetail = () => {
    if (detail.length) {
      blocks.push({ type: 'detail', rows: detail });
      detail = [];
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (isHeading(line)) {
      flushDetail();
      blocks.push({ type: 'heading', title: line.trim().replace(/:\s*$/, '') });
      continue;
    }

    if (isRowGroup(line) || isItem(line)) {
      flushDetail();
      const title = isRowGroup(line) ? line.trim().replace(/:\s*$/, '') : '';
      const items = [];
      let note = '';
      if (isItem(line)) items.push(line);
      while (i + 1 < lines.length && (isItem(lines[i + 1]) || isNote(lines[i + 1]))) {
        i += 1;
        if (isItem(lines[i])) items.push(lines[i]);
        else note = lines[i].trim();
      }
      blocks.push({ type: 'table', title, items, note });
      continue;
    }

    detail.push(line.trim());
  }
  flushDetail();

  const body = (
    <div className="space-y-2.5">
      {blocks.map((block, i) => {
        if (block.type === 'heading') {
          return (
            <p key={i} className="text-xs font-bold text-navy-900 pt-1 first:pt-0">
              {block.title}
            </p>
          );
        }
        if (block.type === 'table') {
          return <ItemTable key={i} title={block.title} items={block.items} note={block.note} />;
        }
        return <DetailRows key={i} rows={block.rows} />;
      })}
    </div>
  );

  return maxHeight ? (
    <div className="overflow-y-auto pr-1" style={{ maxHeight }}>{body}</div>
  ) : body;
}

// A single finding value of unknown shape, shown as words instead of JSON.
export function findingValue(value) {
  if (value === null || value === undefined || value === '') return '\u2014';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (Array.isArray(value)) {
    if (value.length === 0) return '\u2014';
    if (typeof value[0] === 'object') {
      return value
        .slice(0, 3)
        .map((v) => v.description || v.summary || v.label || v.name || v.issue || 'item')
        .join('; ') + (value.length > 3 ? ` (+${value.length - 3} more)` : '');
    }
    return value.join(', ');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => v !== null && typeof v !== 'object');
    if (!entries.length) return `${Object.keys(value).length} fields recorded`;
    return entries
      .slice(0, 3)
      .map(([k, v]) => `${k.replace(/_/g, ' ')} ${findingValue(v)}`)
      .join('; ');
  }
  return String(value);
}

// Compact count + icon row for anomalies and warnings.
export function FindingAlert({ count, children }) {
  if (!count) return null;
  return (
    <p className="mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 flex items-start gap-1.5">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      <span>{children}</span>
    </p>
  );
}
